import React, { useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'markdown-to-jsx';
import './chat.css';

// Robustly extracts the fan-out payload block even when nested code fences appear inside JSON strings.
const extractFanOutPayload = (text) => {
  if (typeof text !== 'string') return null;
  const payloadMarker = 'Payload:';
  const markerIndex = text.indexOf(payloadMarker);
  if (markerIndex === -1) return null;

  const fenceStart = text.indexOf('```json', markerIndex);
  if (fenceStart === -1) return null;

  const firstBrace = text.indexOf('{', fenceStart);
  if (firstBrace === -1) return null;

  let depth = 0;
  let inString = false;
  let jsonEnd = -1;
  let prevChar = '';

  for (let idx = firstBrace; idx < text.length; idx += 1) {
    const ch = text[idx];
    if (inString) {
      if (ch === '"' && prevChar !== '\\') {
        inString = false;
      }
    } else {
      if (ch === '"') {
        inString = true;
      } else if (ch === '{') {
        depth += 1;
      } else if (ch === '}') {
        depth -= 1;
        if (depth === 0) {
          jsonEnd = idx + 1;
          break;
        }
      }
    }
    prevChar = ch;
  }

  if (jsonEnd === -1) return null;

  const jsonText = text.slice(firstBrace, jsonEnd);

  // Determine trailing content after the closing code fence, if present
  let remainder = text.slice(jsonEnd);
  const fenceCloseIdx = remainder.indexOf('```');
  if (fenceCloseIdx !== -1) {
    remainder = remainder.slice(fenceCloseIdx + 3);
  }

  return {
    summary: text.slice(0, markerIndex).trim(),
    jsonText,
    trailing: remainder.trim(),
  };
};

const normalizeForMarkdown = (value) => {
  if (typeof value !== 'string') return value;
  return value.includes('\\n') ? value.replace(/\\n/g, '\n') : value;
};

const parseActionPayload = (raw) => {
  if (raw === null || raw === undefined) return null;

  const buildResult = (payload) => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
    const thoughtValue = payload.thought;
    const actionName = payload.name;
    const parameters = payload.parameters;
    if (thoughtValue === undefined && actionName === undefined) return null;

    let normalizedParameters;
    if (
      typeof parameters === 'string' ||
      typeof parameters === 'number' ||
      typeof parameters === 'boolean' ||
      parameters === null
    ) {
      normalizedParameters = parameters;
    } else if (Array.isArray(parameters) || typeof parameters === 'object') {
      normalizedParameters = parameters;
    } else if (parameters !== undefined) {
      normalizedParameters = String(parameters);
    } else {
      normalizedParameters = undefined;
    }

    return {
      thought: typeof thoughtValue === 'string' ? thoughtValue : thoughtValue !== undefined && thoughtValue !== null ? String(thoughtValue) : '',
      action: {
        name: typeof actionName === 'string' ? actionName : actionName !== undefined && actionName !== null ? String(actionName) : '',
        parameters: normalizedParameters,
      },
    };
  };

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return buildResult(raw);
  }

  if (typeof raw !== 'string') return null;

  let stripped = raw.trim();
  if (!stripped) return null;

  if (stripped.startsWith('```')) {
    const firstNewline = stripped.indexOf('\n');
    if (firstNewline !== -1) {
      stripped = stripped.slice(firstNewline + 1);
    } else {
      stripped = stripped.slice(3);
    }
    if (stripped.endsWith('```')) {
      stripped = stripped.slice(0, -3);
    }
    stripped = stripped.trim();
  }

  try {
    const parsed = JSON.parse(stripped);
    return buildResult(parsed);
  } catch (_err) {
    return null;
  }
};

const buildActionSummaryMap = (transcript) => {
  const summaryMap = new Map();
  if (!Array.isArray(transcript) || transcript.length === 0) {
    return summaryMap;
  }

  let actionsSinceLastUser = [];

  for (let idx = 0; idx < transcript.length; idx += 1) {
    const msg = transcript[idx];
    if (!msg) continue;

    if (msg.role === 'user' || msg.role === 'system') {
      actionsSinceLastUser = [];
      continue;
    }

    if (msg.role === 'assistant') {
      const parsed = parseActionPayload(msg.content);
      if (parsed) {
        actionsSinceLastUser.push(parsed);
        continue;
      }

      const contentText = typeof msg.content === 'string' ? msg.content : '';
      if (contentText.includes('Action_Response for')) {
        continue;
      }

      if (actionsSinceLastUser.length > 0) {
        summaryMap.set(msg, actionsSinceLastUser.slice());
      }
    }
  }

  return summaryMap;
};

function FanOutPayloadBlock({ jsonText }) {
  const formatted = useMemo(() => {
    if (typeof jsonText !== 'string') return '';
    const trimmed = jsonText.trim();
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch (_err) {
      return trimmed;
    }
  }, [jsonText]);

  return (
    <details className="fanout-payload-details">
      <summary>Payload (fan_out_agents)</summary>
      <pre className="fanout-json-block">
        <code>{formatted}</code>
      </pre>
    </details>
  );
}

function FanOutResultView({ summaryText, payload, trailingText, markdownOptions, rawJson }) {
  const tasks = useMemo(() => (Array.isArray(payload?.tasks) ? payload.tasks : []), [payload]);
  const [activeTaskIndex, setActiveTaskIndex] = useState(0);
  const taskCount = tasks.length;

  useEffect(() => {
    setActiveTaskIndex((prev) => {
      if (taskCount === 0) return 0;
      return prev > taskCount - 1 ? 0 : prev;
    });
  }, [taskCount]);

  const activeTask = tasks[activeTaskIndex] || null;
  const cycleTask = (delta) => {
    if (!taskCount) return;
    setActiveTaskIndex((prev) => {
      const next = (prev + delta + taskCount) % taskCount;
      return next;
    });
  };

  const activeTaskActions = useMemo(() => (
    activeTask && Array.isArray(activeTask.actions) ? activeTask.actions : []
  ), [activeTask]);

  const fanInSummary = useMemo(() => {
    if (!payload?.fan_in) return '';
    const fanIn = payload.fan_in;
    if (typeof fanIn === 'string') return fanIn;
    if (fanIn && typeof fanIn === 'object') {
      const messages = Array.isArray(fanIn.messages) ? fanIn.messages : [];
      const assistantMsg = [...messages].reverse().find(
        (msg) => msg && typeof msg.content === 'string' && (msg.role || '').toLowerCase() === 'assistant'
      );
      if (assistantMsg) return assistantMsg.content;
      if (typeof fanIn.summary === 'string') return fanIn.summary;
      if (typeof fanIn.output === 'string') return fanIn.output;
    }
    return '';
  }, [payload]);

  const fanInError = payload?.fan_in && typeof payload.fan_in === 'object' ? payload.fan_in.error : undefined;
  const fanOutId = payload?.fan_out_id;
  const fanInActions = useMemo(() => {
    if (!payload?.fan_in || typeof payload.fan_in !== 'object') return [];
    const source = payload.fan_in;
    if (Array.isArray(source.actions)) return source.actions;
    return [];
  }, [payload]);

  const formatStatus = (value) => (value ? value.toString().toUpperCase() : 'UNKNOWN');
  const rawStatus = (value) => (value ? value.toString().toLowerCase() : 'unknown');

  const renderMetadata = (meta) => {
    if (!meta || typeof meta !== 'object') return null;
    try {
      return JSON.stringify(meta, null, 2);
    } catch (err) {
      return String(meta);
    }
  };

  return (
    <div className="fanout-result">
      <div className="fanout-submessage fanout-submessage--summary">
        <div className="fanout-subheader">
          <span className="fanout-section-title">Fan-out Invocation</span>
          {fanOutId && <span className="fanout-id">ID: {fanOutId}</span>}
        </div>
        {summaryText ? (
          <Markdown options={markdownOptions}>{normalizeForMarkdown(summaryText)}</Markdown>
        ) : (
          <p className="fanout-empty-text">No invocation details provided.</p>
        )}
      </div>

      <div className="fanout-submessage fanout-submessage--tasks">
        <div className="fanout-subheader">
          <span className="fanout-section-title">Parallel Agents</span>
          <span className="fanout-section-meta">{tasks.length || 0} running</span>
        </div>
        {tasks.length === 0 ? (
          <div className="fanout-empty-text">No agent runs were reported.</div>
        ) : (
          <>
            {taskCount > 1 && (
              <div className="fanout-task-controls">
                <button
                  type="button"
                  className="fanout-cycle-btn"
                  onClick={() => cycleTask(-1)}
                  aria-label="Show previous agent"
                >
                  ‹
                </button>
                <span className="fanout-task-position">{activeTaskIndex + 1} / {taskCount}</span>
                <button
                  type="button"
                  className="fanout-cycle-btn"
                  onClick={() => cycleTask(1)}
                  aria-label="Show next agent"
                >
                  ›
                </button>
              </div>
            )}
            <div className="fanout-task-tabs">
              {tasks.map((task, idx) => (
                <button
                  key={`${task?.name || 'task'}-${idx}`}
                  type="button"
                  className={`fanout-task-tab${idx === activeTaskIndex ? ' active' : ''}`}
                  onClick={() => setActiveTaskIndex(idx)}
                >
                  <span className="fanout-task-tab-name">{task?.name || `Agent ${idx + 1}`}</span>
                  <span className={`fanout-task-tab-status status-${rawStatus(task?.status)}`}>
                    {formatStatus(task?.status)}
                  </span>
                </button>
              ))}
            </div>
            {activeTask && (
              <div className="fanout-task-body">
                <div className="fanout-task-meta">
                  <div className="fanout-task-meta-name">
                    <strong>{activeTask.name || `Agent ${activeTaskIndex + 1}`}</strong>
                  </div>
                  <div className="fanout-task-meta-times">
                    {activeTask.started_at && (
                      <span className="fanout-task-timestamp">Start: {activeTask.started_at}</span>
                    )}
                    {activeTask.completed_at && (
                      <span className="fanout-task-timestamp">Finish: {activeTask.completed_at}</span>
                    )}
                  </div>
                </div>
                {activeTask.metadata && (
                  <pre className="fanout-task-metadata">{renderMetadata(activeTask.metadata)}</pre>
                )}
                {activeTask.error && (
                  <div className="fanout-task-error">{activeTask.error}</div>
                )}
                {activeTask.output ? (
                  <Markdown options={markdownOptions}>{normalizeForMarkdown(activeTask.output)}</Markdown>
                ) : (
                  <p className="fanout-task-empty">No assistant response captured.</p>
                )}
                {activeTaskActions.length > 0 && (
                  <details className="fanout-task-actions">
                    <summary>Thoughts &amp; actions ({activeTaskActions.length})</summary>
                    <ol className="fanout-task-actions-list">
                      {activeTaskActions.map((step, stepIdx) => {
                        const thoughtText = normalizeForMarkdown(step?.thought || '');
                        const actionName = step?.action?.name;
                        const actionParams = step?.action?.parameters;
                        let paramsDisplay = '';
                        if (actionParams !== undefined) {
                          if (typeof actionParams === 'string') {
                            paramsDisplay = actionParams;
                          } else {
                            try {
                              paramsDisplay = JSON.stringify(actionParams, null, 2);
                            } catch (_err) {
                              paramsDisplay = String(actionParams);
                            }
                          }
                        }

                        return (
                          <li key={`task-action-${stepIdx}`} className="fanout-task-action-item">
                            {thoughtText && (
                              <div className="fanout-task-thought">
                                <strong>Thought:</strong>
                                <Markdown options={markdownOptions}>{thoughtText}</Markdown>
                              </div>
                            )}
                            {(actionName || paramsDisplay) && (
                              <div className="fanout-task-action">
                                <strong>Action:</strong>
                                <span className="fanout-task-action-name">{actionName || 'Unknown action'}</span>
                                {paramsDisplay && (
                                  <pre className="fanout-task-action-params">{paramsDisplay}</pre>
                                )}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  </details>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="fanout-submessage fanout-submessage--aggregation">
        <div className="fanout-subheader">
          <span className="fanout-section-title">Aggregation</span>
          {payload?.fan_in?.status && (
            <span className={`fanout-section-meta status-${rawStatus(payload.fan_in.status)}`}>
              {formatStatus(payload.fan_in.status)}
            </span>
          )}
        </div>
        {fanInSummary ? (
          <Markdown options={markdownOptions}>{normalizeForMarkdown(fanInSummary)}</Markdown>
        ) : (
          <p className="fanout-empty-text">No aggregation output was returned.</p>
        )}
        {fanInError && <div className="fanout-task-error">{fanInError}</div>}
        {fanInActions.length > 0 && (
          <details className="fanout-task-actions">
            <summary>Aggregation thoughts &amp; actions ({fanInActions.length})</summary>
            <ol className="fanout-task-actions-list">
              {fanInActions.map((step, stepIdx) => {
                const thoughtText = normalizeForMarkdown(step?.thought || '');
                const actionName = step?.action?.name;
                const actionParams = step?.action?.parameters;
                let paramsDisplay = '';
                if (actionParams !== undefined) {
                  if (typeof actionParams === 'string') {
                    paramsDisplay = actionParams;
                  } else {
                    try {
                      paramsDisplay = JSON.stringify(actionParams, null, 2);
                    } catch (_err) {
                      paramsDisplay = String(actionParams);
                    }
                  }
                }

                return (
                  <li key={`fanin-action-${stepIdx}`} className="fanout-task-action-item">
                    {thoughtText && (
                      <div className="fanout-task-thought">
                        <strong>Thought:</strong>
                        <Markdown options={markdownOptions}>{thoughtText}</Markdown>
                      </div>
                    )}
                    {(actionName || paramsDisplay) && (
                      <div className="fanout-task-action">
                        <strong>Action:</strong>
                        <span className="fanout-task-action-name">{actionName || 'Unknown action'}</span>
                        {paramsDisplay && (
                          <pre className="fanout-task-action-params">{paramsDisplay}</pre>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </details>
        )}
        {trailingText && (
          <Markdown options={markdownOptions}>{normalizeForMarkdown(trailingText)}</Markdown>
        )}
      </div>

      {/* Fallback if nothing rendered */}
      {!summaryText && tasks.length === 0 && !fanInSummary && (
        <FanOutPayloadBlock jsonText={rawJson} />
      )}
    </div>
  );
}

/**
 * Generic message list component.
 */
export function ChatMessagesList({
  messages = [],
  loading = false,
  error = '',
  hasMore = false,
  onLoadMore,
  markdownOptions,
  onLoadAgentStep,
  showAgentStepButtons = false,
  chatId,
  containerRef,
  scrollContainerRef, // optional outer scrollable container
  displayedCount = 0,
  allCount = 0,
  alignUserRight = true,
  onUUIDClick,
  seeThoughtsAndActions = false,
  fullTranscript = [],
}) {
  // Helper: extract unique UUID tokens from a text block
  const extractUUIDs = (text) => {
    if (!text || typeof text !== 'string') return [];
    const re = /@@([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})@@/g;
    const out = new Set();
    let m;
    while ((m = re.exec(text)) !== null) out.add(m[1]);
    return Array.from(out);
  };
  // --- Auto-scroll management ---
  const prevLenRef = useRef(messages.length);
  const prevLastMsgRef = useRef(null);
  const prevLastMsgContentLenRef = useRef(0);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const [copiedBlockId, setCopiedBlockId] = useState(null);

  // Track manual scroll position (user scrolled far from bottom -> disable auto scroll until user returns close)
  useEffect(() => {
    const el = (scrollContainerRef?.current) || containerRef?.current;
    if (!el) return;
    const handler = () => {
      const distance = el.scrollHeight - el.clientHeight - el.scrollTop;
      // Increase threshold so large assistant chunks don't wrongly classify user as scrolled up.
      setUserScrolledUp(distance > 800);
    };
    el.addEventListener('scroll', handler, { passive: true });
    // initialize once
    handler();
    return () => el.removeEventListener('scroll', handler);
  }, [scrollContainerRef, containerRef]);

  useEffect(() => {
    const el = (scrollContainerRef?.current) || containerRef?.current;
    if (!el) return;
    const prevLen = prevLenRef.current;
    const newLen = messages.length;
    if (newLen === 0) { prevLenRef.current = newLen; prevLastMsgRef.current = null; prevLastMsgContentLenRef.current = 0; return; }
    const lastMsg = messages[newLen - 1];

    const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    const nearBottom = !userScrolledUp && distanceFromBottom < 200; // widened window
    const appended = newLen > prevLen; // a brand new message object appended

    // Detect streaming growth of last assistant message (same object reference, longer content)
    const prevLastRef = prevLastMsgRef.current;
    const prevContentLen = prevLastMsgContentLenRef.current;
    const currentContentLen = (lastMsg?.content && typeof lastMsg.content === 'string') ? lastMsg.content.length : 0;
    const sameLastObject = prevLastRef === lastMsg; // reference equality
    const grew = sameLastObject && currentContentLen > prevContentLen;

    const role = lastMsg?.role;
    const shouldScroll = (
      // Always scroll for new user message
      (appended && role === 'user') ||
      // For assistant: scroll if new message appended and user hasn't intentionally scrolled far up
      (appended && role === 'assistant' && !userScrolledUp) ||
      // Streaming growth of assistant message while user anchored
      (!appended && role === 'assistant' && grew && !userScrolledUp)
    );

    if (shouldScroll) {
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
    }

    // Update refs for next tick
    prevLenRef.current = newLen;
    prevLastMsgRef.current = lastMsg;
    prevLastMsgContentLenRef.current = currentContentLen;
  }, [messages, containerRef, scrollContainerRef, userScrolledUp]);

  // --- Code block override with copy button & lazy highlight.js ---
  const hljsRef = useRef(null);
  const highlightQueueRef = useRef([]);

  useEffect(() => {
    if (highlightQueueRef.current.length === 0) return;
    let cancelled = false;
    const run = async () => {
      if (!hljsRef.current) {
        try {
          const mod = await import('highlight.js');
          hljsRef.current = mod?.default || mod;
        } catch (e) {
          // highlight.js not available; skip silently
          highlightQueueRef.current = [];
          return;
        }
      }
      if (cancelled) return;
      highlightQueueRef.current.forEach(codeEl => {
        try { hljsRef.current?.highlightElement(codeEl); } catch (_) { /* ignore */ }
      });
      highlightQueueRef.current = [];
    };
    run();
    return () => { cancelled = true; };
  }, [messages]);

  const handleCopy = (text, id) => {
    try { navigator.clipboard.writeText(text); } catch (_) {
      // fallback selection
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (_) { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopiedBlockId(id);
    setTimeout(() => setCopiedBlockId(prev => prev === id ? null : prev), 1400);
  };

  const CodePre = ({ className = '', children, ...rest }) => {
    // children might be <code> element produced by markdown-to-jsx
    let codeText = '';
    let language = '';
    const child = Array.isArray(children) ? children[0] : children;
    const codeElement = (child && child.props && child.props.children) ? child : null;
    if (codeElement && codeElement.props) {
      codeText = typeof codeElement.props.children === 'string' ? codeElement.props.children : (Array.isArray(codeElement.props.children) ? codeElement.props.children.join('') : '');
      const cls = codeElement.props.className || '';
      const match = cls.match(/language-([a-z0-9#+-_]+)/i);
      if (match) language = match[1];
    }
    const blockId = useRef(`cb_${Math.random().toString(36).slice(2, 10)}`);
    const codeRef = useRef(null);
    useEffect(() => {
      if (codeRef.current) highlightQueueRef.current.push(codeRef.current);
    }, [codeText]);

    return (
      <div className={`code-block`} data-lang={language}>
        <button
          type="button"
          className={`copy-code-btn${copiedBlockId === blockId.current ? ' copied' : ''}`}
          onClick={() => handleCopy(codeText, blockId.current)}
          aria-label="Copy code"
        >
          {copiedBlockId === blockId.current ? 'Copied' : 'Copy'}
        </button>
        <pre className={className || ''} {...rest}>
          {codeElement ? (
            <code ref={codeRef} className={codeElement.props.className}>
              {codeText}
            </code>
          ) : children}
        </pre>
      </div>
    );
  };

  const mergedMarkdownOptions = useMemo(() => {
    const base = markdownOptions || {};
    console.log('ChatMessagesList mergedMarkdownOptions', { base, copiedBlockId });
    return {
      ...base,
      overrides: {
        ...(base.overrides || {}),
        pre: { component: CodePre },
      }
    };
  }, [markdownOptions, copiedBlockId]);

  const actionSummaryMap = useMemo(() => {
    if (seeThoughtsAndActions) {
      return new Map();
    }
    return buildActionSummaryMap(fullTranscript);
  }, [fullTranscript, seeThoughtsAndActions]);

  return (
    <div className="messages-list" ref={containerRef}>
      {error && <div className="chat-error-message">{error}</div>}
      {hasMore && (
        <div className="load-more-wrapper" style={{ textAlign: 'center', margin: '10px 0' }}>
          <button className="load-more-button" onClick={onLoadMore} disabled={loading}>
            Load Older Messages ({Math.max(0, allCount.length - displayedCount.length)} more)
          </button>
        </div>
      )}
      {messages.map((m, i) => {
        const isUser = m.role === 'user';
        const isAssistant = m.role === 'assistant';
        const contentStr = String(m.content || '');
        let uuidSearchBase = contentStr;
        // keep modern `.message` classes but also include legacy `.message-bubble`
        const cls = `message message-bubble ${m.role}${isUser && alignUserRight ? ' right' : ''}`;

        const payloadExtraction = extractFanOutPayload(contentStr);
        let contentSegments = [];
        let parsedFanOut = null;
        if (payloadExtraction) {
          const { summary, jsonText, trailing } = payloadExtraction;
          let payloadObject = null;
          try {
            payloadObject = JSON.parse(jsonText);
          } catch (_err) {
            payloadObject = null;
          }

          if (payloadObject) {
            parsedFanOut = {
              summary,
              trailing,
              payload: payloadObject,
              raw: jsonText,
            };

            const uuidPieces = [];
            if (summary) uuidPieces.push(summary);
            if (trailing) uuidPieces.push(trailing);
            const tasks = Array.isArray(payloadObject.tasks) ? payloadObject.tasks : [];
            tasks.forEach((task) => {
              if (task && typeof task.output === 'string') uuidPieces.push(task.output);
              if (task && typeof task.error === 'string') uuidPieces.push(task.error);
              if (task && Array.isArray(task.actions)) {
                task.actions.forEach((step) => {
                  if (step && typeof step.thought === 'string') uuidPieces.push(step.thought);
                  const parameters = step?.action?.parameters;
                  if (typeof parameters === 'string') {
                    uuidPieces.push(parameters);
                  }
                });
              }
            });
            const fanIn = payloadObject.fan_in;
            if (fanIn) {
              if (typeof fanIn === 'string') {
                uuidPieces.push(fanIn);
              } else if (typeof fanIn === 'object') {
                if (Array.isArray(fanIn.messages)) {
                  fanIn.messages.forEach((msg) => {
                    if (msg && typeof msg.content === 'string') uuidPieces.push(msg.content);
                  });
                }
                if (typeof fanIn.summary === 'string') uuidPieces.push(fanIn.summary);
                if (typeof fanIn.output === 'string') uuidPieces.push(fanIn.output);
                if (typeof fanIn.error === 'string') uuidPieces.push(fanIn.error);
                if (Array.isArray(fanIn.actions)) {
                  fanIn.actions.forEach((step) => {
                    if (step && typeof step.thought === 'string') uuidPieces.push(step.thought);
                    const parameters = step?.action?.parameters;
                    if (typeof parameters === 'string') {
                      uuidPieces.push(parameters);
                    }
                  });
                }
              }
            }
            uuidSearchBase = uuidPieces.join('\n');

            contentSegments = [{ type: 'fanoutRich', data: parsedFanOut }];
          } else {
            uuidSearchBase = `${summary}\n${jsonText}\n${trailing}`;
            if (summary) {
              contentSegments.push({ type: 'markdown', text: summary });
            }
            contentSegments.push({ type: 'fanoutRaw', text: jsonText });
            if (trailing) {
              contentSegments.push({ type: 'markdown', text: trailing });
            }
          }
        } else {
          contentSegments = [{ type: 'markdown', text: contentStr }];
        }

        const msgUUIDs = extractUUIDs(uuidSearchBase);
        const summaryActions = actionSummaryMap.get(m) || [];
        const hasFanOutView = contentSegments.some((segment) => segment.type === 'fanoutRich');
        const showCollapsedSummary = !seeThoughtsAndActions && isAssistant && summaryActions.length > 0 && !hasFanOutView;

        return (
          <div key={`${chatId || 'new'}-${i}`} className={cls}>
            <div className="message-content">
              {/* Header row: role label on the left, optional step icon on the right */}
              <div className="message-header">
                <div className="message-role"><strong>{isUser ? 'You' : isAssistant ? 'Assistant' : (m.role || '')}</strong></div>
                {showAgentStepButtons && isAssistant && m._agentStep && (
                  <button
                    className="resume-step-icon-btn"
                    onClick={() => onLoadAgentStep?.(m._agentStep)}
                    title={`Reload chat at step ${m._agentStep}`}
                    aria-label={`Reload chat at step ${m._agentStep}`}
                  >
                    {/* Inline SVG icon (history/restore) */}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M12 5a7 7 0 1 1-6.364 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 8v4l3 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 5v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 5H3l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
              {showCollapsedSummary && (
                <>
                <details className="fanout-task-actions agent-thoughts-summary">
                  <summary>Thoughts &amp; actions ({summaryActions.length})</summary>
                  <ol className="fanout-task-actions-list">
                    {summaryActions.map((step, stepIdx) => {
                      const thoughtText = normalizeForMarkdown(step?.thought || '');
                      const actionName = step?.action?.name;
                      const parametersValue = step?.action?.parameters;
                      let paramsDisplay = '';
                      if (parametersValue !== undefined) {
                        if (typeof parametersValue === 'string') {
                          paramsDisplay = parametersValue;
                        } else if (
                          typeof parametersValue === 'number' ||
                          typeof parametersValue === 'boolean' ||
                          parametersValue === null
                        ) {
                          paramsDisplay = String(parametersValue);
                        } else {
                          try {
                            paramsDisplay = JSON.stringify(parametersValue, null, 2);
                          } catch (_err) {
                            paramsDisplay = String(parametersValue);
                          }
                        }
                      }

                      return (
                        <li key={`agent-summary-${i}-${stepIdx}`} className="fanout-task-action-item">
                          {thoughtText && (
                            <div className="fanout-task-thought">
                              <strong>Thought:</strong>
                              <Markdown options={mergedMarkdownOptions}>{thoughtText}</Markdown>
                            </div>
                          )}
                          {(actionName || paramsDisplay) && (
                            <div className="fanout-task-action">
                              <strong>Action:</strong>
                              <span className="fanout-task-action-name">{actionName || 'Unknown action'}</span>
                              {paramsDisplay && (
                                <pre className="fanout-task-action-params">{paramsDisplay}</pre>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </details>
                {/* add an empty line */}
                <div style={{ height: '1em' }} />
                </>
              )}
              {contentSegments.map((segment, segmentIdx) => {
                if (segment.type === 'fanoutRaw') {
                  return (
                    <FanOutPayloadBlock
                      key={`fanout-${i}-${segmentIdx}`}
                      jsonText={segment.text}
                    />
                  );
                }
                if (segment.type === 'fanoutRich') {
                  return (
                    <FanOutResultView
                      key={`fanout-rich-${i}-${segmentIdx}`}
                      summaryText={segment.data.summary}
                      payload={segment.data.payload}
                      trailingText={segment.data.trailing}
                      markdownOptions={mergedMarkdownOptions}
                      rawJson={segment.data.raw}
                    />
                  );
                }
                return (
                  <Markdown
                    key={`markdown-${i}-${segmentIdx}`}
                    options={mergedMarkdownOptions}
                  >
                    {normalizeForMarkdown(segment.text)}
                  </Markdown>
                );
              })}
              
              {/* Bottom chip bar mirrors the top so users have easy access after reading */}
              {msgUUIDs.length > 0 && (
                <div className="uuid-chipbar bottom">
                  {msgUUIDs.map(u => (
                    <button
                      type="button"
                      key={`bottom-${i}-${u}`}
                      className="uuid-chip"
                      title={`Open Exploration Log (${u})`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onUUIDClick && onUUIDClick(u); }}
                    >
                      {`Explore: ${u.substring(0, 8)}…`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {loading && (
        <div className="loading">
          <span className="loading-spinner" aria-hidden="true" />
          <span>Assistant is thinking...</span>
        </div>
      )}
    </div>
  );
}

export default ChatMessagesList;
